
import { Repository } from './Repository';
import { ChatChannel } from '../models/ChatChannel';
import { ChatChannelUser } from '../models/ChatChannelUser';

export class ChatRepository extends Repository {
  async toggleMute(channel: ChatChannel): Promise<void> {
    try {
      if (channel.isMuted) {
        await channel.channel.unmute();
      } else {
        await channel.channel.mute();
      }
    } catch (err) {
      console.error('Toggle mute error:', err);
    }
  }

  async pin(channel: ChatChannel): Promise<void> {
    try {  
      await channel.channel.pin();  
    } catch (err) {
      console.error('pin error:', err);
    }
  }

  async unpin(channel: ChatChannel): Promise<void> {
    try {  
      await channel.channel.unpin();  
      await channel.channel.watch();
    } catch (err) {
      console.error('unpin error:', err);
    }
  }

  async markRead(channel: ChatChannel): Promise<void> {
    if ((channel.unreadCount ?? 0) !== 0) {
      await channel.channel.markRead();
    }
  }

  async markUnread(channel: ChatChannel, userId: string): Promise<void> {
    if ((channel.unreadCount ?? 0) === 0) {
      let messageId = channel.lastMessage?.id;

      if(channel.channel.state?.read[userId] && channel.channel.state?.read[userId]?.last_read_message_id){
        messageId = channel.channel.state.read[userId].last_read_message_id;
      }

      await channel.channel.markUnread({
        message_id: messageId,
        user_id: userId
      });
    }
  }

  async deleteConnection(channel: ChatChannel): Promise<void> {
    if (channel instanceof ChatChannelUser) {
      try {
        await Repository.client.delete('v1/connections', {
          params: { userId: channel.id }
        });
        this.onChannelRemoved(channel.id || '');
      } catch (err) {
        console.error('Delete connection error:', err);
      }
    }
  }

  async blockUser(channel: ChatChannel): Promise<boolean> {
    if (channel instanceof ChatChannelUser) {
      try {
        await Repository.client.post('v1/blocks', {
          blockedUserId: channel.id
        });
        this.onChannelRemoved(channel.id || '');
        return true;
      } catch (err) {
        console.error('Block user error:', err);
      }
    }
    return false;
  }

  async leaveEventChat(eventId: string): Promise<void> {
    try {
      await Repository.client.delete(`v1/events/${eventId}/rsvps`);
      this.onChannelRemoved(eventId);
    } catch (err) {
      console.error('Leave event chat error:', err);
    }
  }

  async leaveGroupChat(groupChatId: string, currentUserId: string): Promise<void> {
    try {
      await this.removeGroupChatMember(groupChatId, currentUserId);
      this.onChannelRemoved(groupChatId);
    } catch (err) {
      console.error('Leave group chat error:', err);
    }
  }

  async removeGroupChatMember(groupChatId: string, memberId: string): Promise<void> {
    try {
      await Repository.client.delete(`v1/chats/groups/${groupChatId}/members/${memberId}`);
    } catch (err) {
      console.error('Remove group chat member error:', err);
    }
  }

  async deleteGroupChat(groupChatId: string): Promise<void> {
    try {
      await Repository.client.delete(`v1/chats/groups/${groupChatId}`);
      this.onChannelRemoved(groupChatId);
    } catch (err) {
      console.error('Delete group chat error:', err);
    }
  }

  async generateDeeplink(channelType: string, id: string): Promise<string> {
    try {
      const response = await Repository.client.post(`v1/chats/${channelType}/${id}/shareLinks`);
      return response.data.data.link;
    } catch (err) {
      console.error('Generate deeplink error:', err);
      throw err;
    }
  }

  private onChannelRemoved(channelId: string): void {
    // Implement channel removal logic
  }
}

// Create a singleton instance
export const chatRepository = new ChatRepository();